import { useQuery } from '@tanstack/react-query';
import { telegramKeys } from '../telegram.keys';
import { getTelegramBotStatus } from '../telegram.service';
/**
 * React Query hook to check Telegram bot status in a chat
 *
 * Features:
 * - 5 minute cache TTL
 * - Automatic retry with exponential backoff
 * - Graceful error handling
 * - Loading and error states
 *
 * @param options - Query options including username and React Query overrides
 * @returns Query result with bot status and invite URL if needed
 */
export const useTelegramBotStatus = (options = {}) => {
    const { username, ...queryOptions } = options;
    return useQuery({
        queryKey: telegramKeys.botStatus(username || ''),
        queryFn: () => {
            if (!username) {
                throw new Error('Username is required to check bot status');
            }
            return getTelegramBotStatus(username);
        },
        enabled: Boolean(username),
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
        retry: (failureCount, error) => {
            // Don't retry on client errors (4xx)
            if (error instanceof Error && error.message.includes('400')) {
                return false;
            }
            // Retry up to 3 times for other errors
            return failureCount < 3;
        },
        retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff, max 30s
        ...queryOptions,
    });
};
